import { Effect, Option, Schema } from "effect";
import { HttpApiEndpoint, HttpApiGroup, HttpApiSchema } from "effect/unstable/httpapi";
import { ObservabilityHttpErrorResponses } from "../errors/http.ts";
import { TraceValidationError } from "../errors/observability.ts";
import { parseWorkerstationHost, VersionLabel, WorkerHostLabel } from "../station-host.ts";
import { StationId } from "../station-id.ts";
import { TraceSpanRecord, TraceSummary } from "../trace-model.ts";
import { TraceHttpId, TraceProbeId, TraceScopeId } from "./traces.ts";

export const TraceProbeFormat = /* @__PURE__ */ (() => Schema.Literals(["json", "image"]))();
export type TraceProbeFormat = typeof TraceProbeFormat.Type;
const QueryEntries = /* @__PURE__ */ (() => Schema.Struct({
  urls: Schema.Array(Schema.String).check(Schema.isMinLength(1), Schema.isMaxLength(1)),
  formats: Schema.Array(Schema.String).check(Schema.isMaxLength(1)),
  keys: Schema.Array(Schema.Literals(["url", "format"]))
}))();
const invalid = (message: string) => new TraceValidationError({ message });

/** Preserve URL normalization and validation order before any probe I/O. */
export const traceProbeTarget = Effect.fn("TraceProbe.target")(function* (search: URLSearchParams) {
  const entries = yield* Schema.decodeUnknownEffect(QueryEntries)({
    urls: search.getAll("url"), formats: search.getAll("format"), keys: [...search.keys()]
  }).pipe(Effect.mapError(() => invalid("Exactly one url and at most one format query parameter are required.")));
  const format = yield* Schema.decodeUnknownEffect(TraceProbeFormat)(entries.formats[0] ?? "json").pipe(
    Effect.mapError(() => invalid("format must be json or image.")));
  const url = yield* Schema.decodeUnknownEffect(Schema.URLFromString)(entries.urls[0]).pipe(
    Effect.mapError(() => invalid("url must be an absolute HTTPS URL.")));
  if (url.protocol !== "https:") return yield* Effect.fail(invalid("url must use HTTPS."));
  if (url.username || url.password) return yield* Effect.fail(invalid("url must not contain credentials."));
  if (url.port) return yield* Effect.fail(invalid("url must not use a custom port."));
  if (url.hash) return yield* Effect.fail(invalid("url must not contain a fragment."));
  const unavailable = () => invalid("url must target a Workerstation worker or version subdomain.");
  const host = yield* Option.match(Option.fromUndefinedOr(parseWorkerstationHost(url.hostname) ?? undefined), {
    onNone: () => Effect.fail(unavailable()),
    onSome: host => host.kind === "worker" || host.kind === "version" ? Effect.succeed(host) : Effect.fail(unavailable())
  });
  return { url, host, format };
});

const Elapsed = /* @__PURE__ */ (() => Schema.Finite.check(Schema.isGreaterThanOrEqualTo(0)))();
const ProbeError = /* @__PURE__ */ (() => Schema.Struct({ type: Schema.String, message: Schema.String }))();
export const TraceProbePhase = /* @__PURE__ */ (() => Schema.Struct({
  name: Schema.Literals(["profile-waiter-setup", "target-fetch", "response-capture", "tail-profile-collection", "custom-span-collection"]),
  span: Schema.Literals(["trace.profile.prepare", "trace.target.fetch", "trace.response.capture", "trace.tail.collect", "trace.store.poll"]),
  startedAt: Schema.Finite, endedAt: Schema.Finite,
  durationMs: Elapsed, status: Schema.Literals(["ok", "error"]), error: Schema.optionalKey(ProbeError)
}))();
export type TraceProbePhase = typeof TraceProbePhase.Type;

/** Native profile metadata is decoded without dropping its bounded optional fields. */
export const DynamicWorkerTraceProfile = /* @__PURE__ */ (() => Schema.Struct({
  correlationId: TraceProbeId, observedAt: Elapsed,
  worker: Schema.Struct({
    workerId: Schema.String, workspaceId: Schema.optionalKey(Schema.String), scriptName: Schema.optionalKey(Schema.String),
    entrypoint: Schema.optionalKey(Schema.String),
    scriptVersion: Schema.optionalKey(Schema.Struct({ id: Schema.optionalKey(Schema.String), tag: Schema.optionalKey(Schema.String), message: Schema.optionalKey(Schema.String) })),
    dispatchNamespace: Schema.optionalKey(Schema.String), scriptTags: Schema.optionalKey(Schema.Array(Schema.String).check(Schema.isMaxLength(50))),
    scriptTagsTruncated: Schema.optionalKey(Schema.Boolean), durableObjectId: Schema.optionalKey(Schema.String),
    preview: Schema.optionalKey(Schema.Struct({ id: Schema.String, slug: Schema.String, name: Schema.String }))
  }),
  invocation: Schema.Struct({
    eventType: Schema.Literal("fetch"), eventTimestamp: Schema.optionalKey(Elapsed), method: Schema.String, url: Schema.String,
    status: Schema.optionalKey(Schema.Number), outcome: Schema.String, executionModel: Schema.String,
    cpuTimeMs: Elapsed, wallTimeMs: Elapsed, cpuUtilizationPercent: Schema.optionalKey(Elapsed), truncated: Schema.Boolean
  }),
  logs: Schema.Array(Schema.Struct({ timestamp: Elapsed, level: Schema.String, message: Schema.String })).check(Schema.isMaxLength(100)),
  logsTruncated: Schema.optionalKey(Schema.Boolean),
  exceptions: Schema.Array(Schema.Struct({ timestamp: Elapsed, name: Schema.String, message: Schema.String, stack: Schema.optionalKey(Schema.String) })).check(Schema.isMaxLength(50)),
  exceptionsTruncated: Schema.optionalKey(Schema.Boolean),
  diagnostics: Schema.Array(Schema.Struct({ timestamp: Elapsed, channel: Schema.String, message: Schema.Unknown })).check(Schema.isMaxLength(50)),
  diagnosticsTruncated: Schema.optionalKey(Schema.Boolean), tailAttributes: Schema.optionalKey(Schema.Unknown)
}))();
export type DynamicWorkerTraceProfile = typeof DynamicWorkerTraceProfile.Type;

export const TraceProfileWaitResult = /* @__PURE__ */ (() => Schema.Struct({
  outcome: Schema.Literals(["delivered", "expired", "cancelled"]), elapsedMs: Elapsed,
  profile: Schema.optionalKey(DynamicWorkerTraceProfile)
}))();
export type TraceProfileWaitResult = typeof TraceProfileWaitResult.Type;

export const CorrelatedTraceResult = /* @__PURE__ */ (() => Schema.Struct({
  workspaceId: TraceScopeId, traceId: TraceHttpId, traceIds: Schema.Array(TraceHttpId).check(Schema.isMaxLength(100)),
  summary: TraceSummary, spans: Schema.Array(TraceSpanRecord).check(Schema.isMaxLength(1_000)), truncated: Schema.Boolean
}))();
export type CorrelatedTraceResult = typeof CorrelatedTraceResult.Type;

export const TraceProbeResponse = /* @__PURE__ */ (() => Schema.Struct({
  correlationId: TraceProbeId, workerVersion: Schema.NullOr(Schema.Struct({ id: Schema.String })),
  target: Schema.Union([
    Schema.Struct({ url: Schema.String, kind: Schema.Literal("worker"), stationId: StationId, workerName: WorkerHostLabel }),
    Schema.Struct({ url: Schema.String, kind: Schema.Literal("version"), stationId: StationId, workspaceId: WorkerHostLabel, version: VersionLabel })
  ]),
  response: Schema.NullOr(Schema.Struct({
    status: Schema.Int, statusText: Schema.String, headers: Schema.Record(Schema.String, Schema.String),
    body: Schema.String, byteLength: Elapsed, truncated: Schema.optionalKey(Schema.Literal(true))
  })),
  error: Schema.NullOr(ProbeError),
  profiling: Schema.Struct({
    collection: Schema.Struct({ outcome: Schema.Literals(["delivered", "expired", "cancelled", "unavailable", "failed"]), elapsedMs: Elapsed }),
    invocation: Schema.NullOr(DynamicWorkerTraceProfile)
  }),
  trace: Schema.Struct({
    collection: Schema.Struct({ outcome: Schema.Literals(["found", "expired", "unavailable", "failed"]), elapsedMs: Elapsed }),
    customSpans: Schema.NullOr(CorrelatedTraceResult), phases: Schema.Array(TraceProbePhase).check(Schema.isMaxLength(5))
  }),
  timing: Schema.Struct({ startedAt: Schema.Finite, endedAt: Schema.Finite, durationMs: Elapsed })
}))();
export type TraceProbeResponse = typeof TraceProbeResponse.Type;

const ProbeSvg = /* @__PURE__ */ (() => Schema.String.pipe(HttpApiSchema.asText({ contentType: "image/svg+xml; charset=utf-8" })))();
export const TraceProbeApi = HttpApiGroup.make("traceProbe").add(
  HttpApiEndpoint.get("probe", "/api/trace", {
    query: Schema.Struct({ url: Schema.String, format: Schema.optionalKey(TraceProbeFormat) }),
    success: [TraceProbeResponse, TraceProbeResponse.annotate({ httpApiStatus: 502 }), ProbeSvg, ProbeSvg.annotate({ httpApiStatus: 502 })],
    error: [...ObservabilityHttpErrorResponses, Schema.Struct({ error: Schema.String }).annotate({ httpApiStatus: 503 })]
  })
);
